import { useEffect } from 'react';

import { ApiError } from 'lib/errors';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiNonRetryableResponseError from 'lib/errors/ApiNonRetryableResponseError';
import ApiRequestError from 'lib/errors/ApiRequestError';
import ApiResponseError from 'lib/errors/ApiResponseError';

interface UseApiErrorHandlerArgs {
  error: Error | ApiError | null | undefined;
  /**
   * A function for handling or showing messages on error.
   */
  onError: (error: ApiResponseError) => void;
  onNetworkError: (error: ApiNetworkError) => void;
  onNonRetryableError?: (error: ApiNonRetryableResponseError) => void;
  onRequestError?: (error: ApiRequestError) => void;
}

/**
 * A hook that triggers an error message display when it receives an error.
 * Meant to be used in conjunction with `useApiCallback` to flash messages on error.
 */
export default ({
  error,
  onError,
  onNetworkError,
  onNonRetryableError,
  onRequestError,
}: UseApiErrorHandlerArgs) => {
  useEffect(() => {
    if (error == null) {
      // Do nothing
      return;
    }
    const isApiError = typeof error === 'object' && error != null && 'method' in error;
    if (!isApiError) {
      throw error;
    }

    if (error instanceof ApiNetworkError) {
      onNetworkError?.(error);
    } else if (error instanceof ApiResponseError) {
      onError?.(error);
    } else if (error instanceof ApiRequestError) {
      onRequestError?.(error);
    } else if (error instanceof ApiNonRetryableResponseError) {
      onNonRetryableError?.(error);
    } else {
      throw error;
    }
  }, [error]);
};
